/**
 * admin/AdminOrders.js — what people have bought.
 *
 * An order is a record of a moment: the names and prices were copied in
 * when it was placed, so renaming a product later doesn't rewrite
 * someone's receipt. Nothing here edits those lines — only the status
 * and a private note move.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  HiOutlineReceiptPercent, HiOutlineArrowDownTray, HiOutlineTruck,
  HiOutlineBuildingStorefront, HiOutlineChevronDown, HiOutlineBanknotes,
} from "react-icons/hi2";
import toast from "react-hot-toast";

import AdminLayout from "./AdminLayout";
import { ordersAPI } from "../services/api";
import { useCurrentAdmin, hasPermission } from "./permissions";
import "./AdminOrders.css";

/* Two paths through the same list: posted, or picked up. */
const FLOW = {
  ship:    ["new", "paid", "packed", "shipped"],
  collect: ["new", "paid", "packed", "collected"],
};
const ALL = ["new", "paid", "packed", "shipped", "collected", "cancelled"];

const LABEL = {
  new: "New", paid: "Paid", packed: "Packed",
  shipped: "Shipped", collected: "Collected", cancelled: "Cancelled",
};

const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 });

function OrderCard({ order, canEdit, onChanged }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(order.admin_notes || "");
  const [busy, setBusy] = useState(false);

  const steps = FLOW[order.fulfilment] || FLOW.ship;

  const patch = async (payload, said) => {
    setBusy(true);
    try {
      await ordersAPI.update(order.id, payload);
      toast.success(said);
      onChanged();
    } catch (err) { toast.error(err.message || "Couldn't save that."); }
    finally { setBusy(false); }
  };

  const next = order.status === "cancelled" ? null : steps[steps.indexOf(order.status) + 1];

  return (
    <div className="acard aor__card">
      <button type="button" className="aor__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <div className="aor__head-main">
          <p className="aor__ref">
            {order.reference}
            <span className={`awk__status aor__status--${order.status}`}>{LABEL[order.status]}</span>
          </p>
          <p className="aor__who">
            {order.name} · {order.email}
            {order.phone ? ` · ${order.phone}` : ""}
          </p>
        </div>

        <div className="aor__head-side">
          <span className="aor__flow" title={order.fulfilment === "ship" ? "To be posted" : "Collecting on arrival"}>
            {order.fulfilment === "ship" ? <HiOutlineTruck /> : <HiOutlineBuildingStorefront />}
            {order.fulfilment === "ship" ? "Ship" : "Collect"}
          </span>
          <span className="aor__total">{money(order.total)}</span>
          <span className="aor__when">{order.placed_at}</span>
          <HiOutlineChevronDown className={`aor__chev ${open ? "is-open" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="aor__body">
          <div className="aor__cols">
            <div>
              <p className="aor__sub">Items</p>
              <ul className="aor__items">
                {order.items.map((i, n) => (
                  <li key={n}>
                    <span className="aor__item-qty">{i.quantity}×</span>
                    <span className="aor__item-name">
                      {i.name}
                      {(i.colour || i.size) && (
                        <em> — {[i.colour, i.size].filter(Boolean).join(" / ")}</em>
                      )}
                    </span>
                    <span className="aor__item-price">{money(i.line_total)}</span>
                  </li>
                ))}
              </ul>

              <dl className="aor__sums">
                <div><dt>Subtotal</dt><dd>{money(order.subtotal)}</dd></div>
                <div><dt>Shipping</dt><dd>{order.shipping > 0 ? money(order.shipping) : "—"}</dd></div>
                <div className="aor__sums-total"><dt>Total</dt><dd>{money(order.total)}</dd></div>
              </dl>
            </div>

            <div>
              <p className="aor__sub">{order.fulfilment === "ship" ? "Send to" : "Collecting"}</p>
              <p className="aor__addr">
                {order.fulfilment === "ship"
                  ? (order.address || "No address given.")
                  : "Picking up in person."}
              </p>

              {order.notes && (
                <>
                  <p className="aor__sub">What they said</p>
                  <p className="aor__addr">{order.notes}</p>
                </>
              )}

              <p className="aor__sub">Private note</p>
              {canEdit ? (
                <>
                  <textarea
                    className="arb__form-input"
                    rows={3}
                    value={note}
                    placeholder="Tracking number, who packed it — the customer never sees this."
                    onChange={(e) => setNote(e.target.value)}
                  />
                  <button
                    type="button"
                    className="a-btn a-btn--outline apr__mini"
                    disabled={busy || note === (order.admin_notes || "")}
                    onClick={() => patch({ admin_notes: note }, "Note saved.")}
                  >
                    Save note
                  </button>
                </>
              ) : (
                <p className="aor__addr">{order.admin_notes || "—"}</p>
              )}
            </div>
          </div>

          {canEdit && (
            <div className="aor__actions">
              {next && (
                <button type="button" className="a-btn a-btn--rust" disabled={busy}
                  onClick={() => patch({ status: next }, `Marked ${LABEL[next].toLowerCase()}.`)}>
                  Mark {LABEL[next].toLowerCase()}
                </button>
              )}
              <div className="aor__jump">
                <label htmlFor={`st-${order.id}`}>Status</label>
                <select id={`st-${order.id}`} className="arb__form-input" value={order.status} disabled={busy}
                  onChange={(e) => patch({ status: e.target.value }, "Status changed.")}>
                  {ALL.map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminOrders() {
  const { admin } = useCurrentAdmin();
  const canEdit = hasPermission(admin, "shop.edit");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ordersAPI.list({ status, q });
      setOrders(res.data || []);
    } catch (err) { toast.error(err.message || "Couldn't load orders."); }
    finally { setLoading(false); }
  }, [status, q]);

  /* The search hits the server, so wait for typing to stop. */
  useEffect(() => {
    const t = window.setTimeout(load, q ? 350 : 0);
    return () => window.clearTimeout(t);
  }, [load, q]);

  const stats = useMemo(() => {
    const open = orders.filter((o) => o.status !== "cancelled" && o.status !== "shipped" && o.status !== "collected");
    const taken = orders.filter((o) => o.status !== "cancelled").reduce((n, o) => n + o.total, 0);
    return { open: open.length, taken, all: orders.length };
  }, [orders]);

  const download = async () => {
    try { await ordersAPI.exportCsv(); }
    catch (err) { toast.error(err.message || "Export failed."); }
  };

  return (
    <AdminLayout pageTitle="Orders" pageSubtitle="Everything bought from the shop."
      permission="shop.view" feature="shop">
      {loading ? (
        <div className="arb__loading"><div className="acr__spinner" /></div>
      ) : (
        <>
          <div className="aap__stats-row" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(var(--admin-rust-rgb),0.1)" }}>
                  <HiOutlineReceiptPercent style={{ color: "var(--admin-rust)", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">Needs doing</p>
              </div>
              <p className="aap__stat-value">{stats.open}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(22,163,74,0.1)" }}>
                  <HiOutlineBanknotes style={{ color: "#16a34a", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">Taken</p>
              </div>
              <p className="aap__stat-value">{money(stats.taken)}</p>
            </div>
            <div className="acard aap__stat-card">
              <div className="aap__stat-top">
                <div className="aap__stat-icon" style={{ background: "rgba(107,98,88,0.12)" }}>
                  <HiOutlineTruck style={{ color: "#6b6258", fontSize: "1.1rem" }} />
                </div>
                <p className="aap__stat-label">Orders shown</p>
              </div>
              <p className="aap__stat-value">{stats.all}</p>
            </div>
          </div>

          <div className="awk__toolbar">
            <div className="ars__type-row">
              {[{ v: "", label: "All" }, ...ALL.map((s) => ({ v: s, label: LABEL[s] }))].map((s) => (
                <button key={s.label} type="button"
                  className={`ars__type-btn ${status === s.v ? "ars__type-btn--active" : ""}`}
                  onClick={() => setStatus(s.v)}>{s.label}</button>
              ))}
            </div>
            <input className="arb__form-input awk__search" value={q}
              placeholder="Reference, name or email…" onChange={(e) => setQ(e.target.value)} />
            <button type="button" className="a-btn a-btn--outline" onClick={download}>
              <HiOutlineArrowDownTray /> Export CSV
            </button>
          </div>

          {orders.length === 0 ? (
            <div className="acard awk__empty-card">
              <HiOutlineReceiptPercent />
              <p>{q || status ? "Nothing matches that." : "No orders yet."}</p>
            </div>
          ) : (
            <div className="aor__list">
              {orders.map((o) => (
                <OrderCard key={o.id} order={o} canEdit={canEdit} onChanged={load} />
              ))}
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
